/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { InfoPopup } from "../InfoPopup";
import { YearRoulette } from "../YearRoulette";
import { Roulette } from "../Roulette";
import { Chip } from "../Chip";
import { Button } from "../Button";
import {
  faChevronLeft,
  faChevronRight,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./DateRangePicker.css";

interface DateRangePickerProps {
  initialStartDate?: string;
  initialEndDate?: string;
  onChange: (startDate: Date, endDate: Date) => void;
}

type MonthName =
  | "Janeiro"
  | "Fevereiro"
  | "Março"
  | "Abril"
  | "Maio"
  | "Junho"
  | "Julho"
  | "Agosto"
  | "Setembro"
  | "Outubro"
  | "Novembro"
  | "Dezembro";

const Months: MonthName[] = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

type CalendarDay = {
  date: Date;
  isCurrentMonth: boolean;
};

function generateCalendarDays(year: number, month: number): CalendarDay[] {
  const days: CalendarDay[] = [];

  const firstDayOfMonth = new Date(year, month, 1);
  const firstWeekDay = firstDayOfMonth.getDay();

  const gridStartDate = new Date(year, month, 1 - firstWeekDay);

  for (let i = 0; i < 42; i++) {
    const date = new Date(
      gridStartDate.getFullYear(),
      gridStartDate.getMonth(),
      gridStartDate.getDate() + i,
    );

    days.push({
      date,
      isCurrentMonth: date.getMonth() === month,
    });
  }

  return days;
}

function generateCalendarDaysClassName(
  day: CalendarDay,
  currentDay: Date,
  minimumDate?: Date,
): string {
  const classnames = ["dateRangePickerCalendarDayCell"];

  const isBeforeMinimumDate =
    minimumDate !== undefined && day.date < minimumDate;

  const isNotCurrentMonth = !day.isCurrentMonth;

  const isToday = day.date.toDateString() === currentDay.toDateString();

  if (isBeforeMinimumDate || isNotCurrentMonth) {
    classnames.push("dateRangePickerCalendarDayCellMuted");
  } else if (isToday) {
    classnames.push("dateRangePickerCalendarDayCellActivate");
  }

  return classnames.join(" ");
}

function checkIfSelectedDateIsValid(
  day: CalendarDay,
  currentDay: Date,
  minimumDate?: Date,
): boolean {
  const listCheck: boolean[] = [
    minimumDate !== undefined && day.date < minimumDate,
    !day.isCurrentMonth,
    day.date.toDateString() === currentDay.toDateString(),
  ];

  return listCheck.every((v) => !v);
}

const DateRangePickerCalendarContentCard: React.FC<{
  period: "start" | "end";
  year: number;
  month: number;
  day: number;
  parentRef: React.RefObject<HTMLDivElement>;
  onChangeYear: (year: number) => void;
  onChangeMonth: (month: number) => void;
  onChangeDay: (day: number) => void;
  onConclude?: () => void;
  initialStartDate?: Date;
}> = ({
  period,
  year,
  month,
  day,
  parentRef,
  onChangeYear,
  onChangeMonth,
  onChangeDay,
  onConclude,
  initialStartDate,
}) => {
  const popupIdRef = React.useRef(Math.random().toString(36).substring(2));
  const currentYearRef = React.useRef(
    initialStartDate ? new Date(initialStartDate).getFullYear() : year,
  );
  const currentMonthRef = React.useRef(
    initialStartDate ? new Date(initialStartDate).getMonth() : month,
  );
  const activateDateRef = React.useRef(
    initialStartDate ? new Date(initialStartDate) : new Date(year, month, day),
  );

  return (
    <div
      className={`dateRangePickerCalendarCard dateRangePickerCalendarCard${
        period === "start" ? "Initial" : "End"
      }`}
    >
      <span className="dateRangePickerCalendarLabel">
        Data {period === "start" ? "Inicial" : "Final"}
      </span>
      <hr className="dateRangePickerCalendarSeparator" />
      <div className="dateRangePickerCalendarCurrentRange">
        <InfoPopup
          position="se"
          id={popupIdRef.current}
          classNameContainer="dateRangePickerCalendarYearSelectInfoPopup"
          classNameBox="dateRangePickerCalendarYearSelectInfoPopupBox"
          triggerElement={
            <Chip type="button" class="dateRangePickerCalendarRollete">
              {Months[currentMonthRef.current]}
            </Chip>
          }
          withArrow={true}
          triggerAction="click"
          onBlur={() => onChangeMonth(currentMonthRef.current)}
        >
          <Roulette
            initialIndex={currentMonthRef.current}
            options={Months}
            onChange={(index, _) => (currentMonthRef.current = index)}
          />
        </InfoPopup>
        <InfoPopup
          position="sw"
          id={popupIdRef.current}
          classNameContainer="dateRangePickerCalendarYearSelectInfoPopup"
          classNameBox="dateRangePickerCalendarYearSelectInfoPopupBox"
          triggerElement={
            <Chip type="button" class="dateRangePickerCalendarRollete">
              {year}
            </Chip>
          }
          withArrow={true}
          triggerAction="click"
          onBlur={() => onChangeYear(currentYearRef.current)}
        >
          <YearRoulette
            year={currentYearRef.current}
            minYear={2024}
            maxYear={new Date().getFullYear()}
            onChange={(year) => (currentYearRef.current = year)}
          />
        </InfoPopup>
      </div>
      <div className="dateRangePickerCalendarDay">
        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
          <div key={d} className="dateRangePickerCalendarDayHeader">
            {d}
          </div>
        ))}

        {generateCalendarDays(year, month).map((d) => (
          <div
            key={d.date.toISOString()}
            className={generateCalendarDaysClassName(
              d,
              activateDateRef.current,
              initialStartDate,
            )}
            onClick={() => {
              if (
                !checkIfSelectedDateIsValid(
                  d,
                  activateDateRef.current,
                  initialStartDate,
                )
              )
                return;
              activateDateRef.current = d.date;
              onChangeDay(d.date.getDate());
            }}
          >
            {d.date.getDate()}
          </div>
        ))}
      </div>
      {period === "start" ? (
        <div className="dateRangePickerCalendarFooterActions">
          <Button
            class="dateRangePickerCalendarFooterActionButtonMove"
            iconSlot="end"
            icon={
              <FontAwesomeIcon
                icon={faChevronRight}
                style={{ fontSize: "14px" }}
              />
            }
            callback={() => {
              if (!parentRef.current) return;
              parentRef.current.style.transform = "rotateY(-180deg)";
            }}
          >
            Próximo
          </Button>
        </div>
      ) : (
        <div className="dateRangePickerCalendarFooterActions">
          <Button
            class="dateRangePickerCalendarFooterActionButtonMove"
            iconSlot="start"
            icon={
              <FontAwesomeIcon
                icon={faChevronLeft}
                style={{ fontSize: "14px" }}
              />
            }
            callback={() => {
              if (!parentRef.current) return;
              parentRef.current.style.transform = "rotateY(0deg)";
            }}
          >
            Voltar
          </Button>
          <Button
            class="dateRangePickerCalendarFooterActionButtonConclude"
            type="success"
            iconSlot="end"
            icon={
              <FontAwesomeIcon
                icon={faPaperPlane}
                style={{ fontSize: "16px" }}
              />
            }
            callback={() => {
              if (!onConclude) return;
              onConclude();
            }}
          >
            Concluir
          </Button>
        </div>
      )}
    </div>
  );
};

export const DateRangePickerCalendar: React.FC<DateRangePickerProps> = ({
  initialStartDate,
  initialEndDate,
  onChange,
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const labelRef = React.useRef<HTMLDivElement>(null);

  const DateRangePickerCalendarContent: React.FC = () => {
    const initialStartDateObj = initialStartDate
      ? new Date(initialStartDate)
      : new Date();
    const initialEndDateObj = initialEndDate
      ? new Date(initialEndDate)
      : new Date();

    const [initialCurrentDay, setInitialCurrentDay] = React.useState<number>(
      initialStartDateObj.getDate(),
    );
    const [initialCurrentMonth, setInitialCurrentMonth] =
      React.useState<number>(initialStartDateObj.getMonth());
    const [initialCurrentYear, setInitialCurrentYear] = React.useState<number>(
      initialStartDateObj.getFullYear(),
    );
    const [endCurrentYear, setEndCurrentYear] = React.useState<number>(
      initialEndDateObj.getFullYear(),
    );
    const [endCurrentMonth, setEndCurrentMonth] = React.useState<number>(
      initialEndDateObj.getMonth(),
    );
    const [endCurrentDay, setEndCurrentDay] = React.useState<number>(
      initialEndDateObj.getDate(),
    );
    const parentRef = React.useRef<HTMLDivElement>(null);

    function closeCalendarOnClickOutside(event: MouseEvent) {
      if (!parentRef.current) return;

      const target = event.target as Node;

      const infoPopupBox = document.querySelector(".infoPopupBox");
      if (infoPopupBox) {
        return;
      }

      if (labelRef.current && labelRef.current.contains(target)) {
        return;
      }

      if (!parentRef.current.contains(target)) {
        setIsOpen(false);
      }
    }

    React.useEffect(() => {
      document.addEventListener("mousedown", closeCalendarOnClickOutside);
      return () => {
        document.removeEventListener("mousedown", closeCalendarOnClickOutside);
      };
    }, []);

    if (!isOpen) {
      return <></>;
    }

    return (
      <div className="dateRangePickerCalendarContainer">
        <div className="dateRangePickerCalendarContainerInner" ref={parentRef}>
          <DateRangePickerCalendarContentCard
            period="start"
            year={initialCurrentYear}
            month={initialCurrentMonth}
            day={initialCurrentDay}
            parentRef={parentRef}
            onChangeYear={(year) => setInitialCurrentYear(year)}
            onChangeMonth={(month) => setInitialCurrentMonth(month)}
            onChangeDay={(day) => setInitialCurrentDay(day)}
          />
          <DateRangePickerCalendarContentCard
            period="end"
            year={endCurrentYear}
            month={endCurrentMonth}
            day={endCurrentDay}
            parentRef={parentRef}
            onChangeYear={(year) => setEndCurrentYear(year)}
            onChangeMonth={(month) => setEndCurrentMonth(month)}
            onChangeDay={(day) => setEndCurrentDay(day)}
            initialStartDate={
              new Date(
                initialCurrentYear,
                initialCurrentMonth,
                initialCurrentDay,
              )
            }
            onConclude={() => {
              const newStartDate = new Date(
                initialCurrentYear,
                initialCurrentMonth,
                initialCurrentDay,
              );
              const newEndDate = new Date(
                endCurrentYear,
                endCurrentMonth,
                endCurrentDay,
              );
              setIsOpen(false);
              onChange(newStartDate, newEndDate);
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="dateRangePickerContainer">
      <span
        className="dateRangePickerLabel"
        onClick={() => setIsOpen(!isOpen)}
        ref={labelRef}
      >
        Selecionar intervalo de datas
      </span>
      <div className="dateRangePickerCalendarInput">
        <DateRangePickerCalendarContent />
      </div>
    </div>
  );
};
